"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, type RefObject } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import { getNavIcon } from "@/content/nav-icons";
import { mainNav } from "@/content/navigation";
import { NavDonateButton } from "@/components/layout/NavDonateButton";
import { EASE_OUT_EXPO } from "@/components/motion/constants";
import {
  getStaggerContainerVariants,
  getStaggerItemVariants,
} from "@/components/motion/variants";
import { cn } from "@/lib/utils";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";

interface MobileNavOverlayProps {
  open: boolean;
  onClose: () => void;
  menuButtonRef: RefObject<HTMLButtonElement | null>;
  donateVariant: "green" | "accent";
  donateLabel?: string;
}

export function MobileNavOverlay({
  open,
  onClose,
  menuButtonRef,
  donateVariant,
  donateLabel,
}: MobileNavOverlayProps) {
  const pathname = usePathname();
  const reduced = useReducedMotion();
  const overlayRef = useRef<HTMLDivElement>(null);
  const firstLinkRef = useRef<HTMLAnchorElement>(null);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    requestAnimationFrame(() => firstLinkRef.current?.focus());

    const menuButton = menuButtonRef.current;

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      menuButton?.focus();
    };
  }, [open, onClose, menuButtonRef]);

  useEffect(() => {
    if (!open || !overlayRef.current) return;

    const panel = overlayRef.current.querySelector<HTMLElement>(
      '[data-mobile-nav-panel="true"]'
    );
    if (!panel) return;

    const focusable = panel.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const onTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || focusable.length === 0) return;

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener("keydown", onTab);
    return () => document.removeEventListener("keydown", onTab);
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={overlayRef}
          role="dialog"
          id="mobile-nav-menu"
          aria-modal="true"
          aria-label="Navigation menu"
          className="fixed inset-0 z-[60] lg:hidden"
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduced ? undefined : { opacity: 0 }}
          transition={{ duration: 0.3, ease: EASE_OUT_EXPO }}
        >
          <motion.button
            type="button"
            tabIndex={-1}
            aria-label="Close menu"
            className="absolute inset-0 bg-[#0a0a0a]/90 backdrop-blur-xl"
            onClick={onClose}
            initial={reduced ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reduced ? undefined : { opacity: 0 }}
          />

          <motion.div
            data-mobile-nav-panel="true"
            className="relative flex h-full flex-col px-6 pb-[max(2rem,env(safe-area-inset-bottom))] pt-[max(5.5rem,env(safe-area-inset-top))] text-white"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={getStaggerContainerVariants(reduced)}
          >
            <motion.div
              className="mb-4 flex shrink-0 justify-end"
              variants={getStaggerItemVariants(reduced)}
            >
              <button
                type="button"
                onClick={onClose}
                aria-label="Close menu"
                className="rounded-full p-2.5 text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                <X size={28} />
              </button>
            </motion.div>

            <nav className="flex min-h-0 flex-1 flex-col justify-start gap-0.5 overflow-y-auto overscroll-contain py-2">
              {mainNav.map(({ href, label }, index) => {
                const active =
                  href === "/"
                    ? pathname === "/"
                    : pathname.startsWith(href);
                const Icon = getNavIcon(href);

                return (
                  <motion.div
                    key={href}
                    variants={getStaggerItemVariants(reduced)}
                    className="origin-left"
                    whileTap={reduced ? undefined : { scale: 0.98 }}
                    transition={{ type: "spring", stiffness: 420, damping: 28 }}
                  >
                    <Link
                      ref={index === 0 ? firstLinkRef : undefined}
                      href={href}
                      onClick={onClose}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex items-center gap-3 rounded-2xl px-2 py-2 text-2xl font-medium tracking-tight transition-colors sm:text-3xl",
                        active
                          ? "text-white underline decoration-white/50 underline-offset-4"
                          : "text-white/80 hover:text-white"
                      )}
                    >
                      <motion.span
                        className="inline-flex shrink-0"
                        initial={reduced ? false : { opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          duration: reduced ? 0 : 0.28,
                          delay: reduced ? 0 : 0.04 * index,
                          ease: EASE_OUT_EXPO,
                        }}
                      >
                        <Icon
                          size={24}
                          aria-hidden
                          strokeWidth={2}
                          className="h-6 w-6 opacity-80 sm:h-7 sm:w-7"
                        />
                      </motion.span>
                      {label}
                    </Link>
                  </motion.div>
                );
              })}
            </nav>

            <motion.div
              className="mt-4 shrink-0 border-t border-white/15 pt-4"
              variants={getStaggerItemVariants(reduced)}
            >
              <NavDonateButton
                fullWidth
                variant={donateVariant}
                label={donateLabel}
                onClick={onClose}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
